/**
 * Live bed-count subscription (M5-T03). Opens the `/api/v1/live` WebSocket and
 * patches every cached hospital query in place when a `bed_update` arrives, so
 * the bed badges on the list and profile update within ~2s — no refetch.
 *
 * Local interface only (the bed-finder); pass `enabled = false` on Tourism.
 * Resilient by design: reconnects with capped backoff, and any failure is
 * silent — the REST snapshot + the backend's 30s cache TTL remain the source
 * of truth, so a dropped socket degrades to "fresh enough", never to broken.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import type { HospitalListItem } from '../api/hospitals'
import { liveBedsUrl, parseBedUpdate, type BedUpdateEvent } from '../api/live'

const MAX_BACKOFF_MS = 30_000

export function useLiveBeds(enabled = true): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return

    let socket: WebSocket | null = null
    let stopped = false
    let attempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined

    function applyUpdate(evt: BedUpdateEvent): void {
      // Every cached list query (one per chip/geo combination) keyed
      // ['hospitals', ...]: swap the matching hospital's bed state.
      queryClient.setQueriesData<HospitalListItem[]>(
        { queryKey: ['hospitals'] },
        (old) => {
          if (!old) return old
          let changed = false
          const next = old.map((h) => {
            if (h.id !== evt.hospital_id) return h
            changed = true
            return { ...h, beds: evt.beds }
          })
          return changed ? next : old
        },
      )
      // Single-profile queries keyed ['hospital', slug].
      queryClient.setQueriesData<HospitalListItem>(
        { queryKey: ['hospital'] },
        (old) =>
          old && old.id === evt.hospital_id ? { ...old, beds: evt.beds } : old,
      )
    }

    function connect(): void {
      if (stopped) return
      socket = new WebSocket(liveBedsUrl())
      socket.onopen = (): void => {
        attempt = 0
      }
      socket.onmessage = (event: MessageEvent): void => {
        if (typeof event.data !== 'string') return
        const evt = parseBedUpdate(event.data)
        if (evt) applyUpdate(evt)
      }
      socket.onclose = (): void => {
        if (stopped) return
        const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS)
        attempt += 1
        reconnectTimer = setTimeout(connect, delay)
      }
      socket.onerror = (): void => {
        socket?.close() // triggers onclose -> backoff reconnect
      }
    }

    connect()

    return (): void => {
      stopped = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      socket?.close()
    }
  }, [queryClient, enabled])
}
